import React, { useEffect, useState, Component } from 'react';
import { X, CheckCircle2, Mail } from 'lucide-react';
import confetti from 'canvas-confetti';
import { Button } from './ui/Button';
const SUPPORT_EMAIL = 'support@zanoplatform.com';
// Web3Forms delivers submissions straight to your inbox — no email client.
// Get a free access key at https://web3forms.com and set its destination to
// support@zanoplatform.com, then paste the key below.
const WEB3FORMS_ACCESS_KEY = 'daa0e880-90c5-42b7-911c-ac1a7e0e4820';
export const MarketplaceModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Reset back to the form whenever the modal is fully closed.
  useEffect(() => {
    if (!isOpen) {
      const t = window.setTimeout(() => {
        setSubmitted(false);
        setError(null);
        setSubmitting(false);
      }, 250);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zano-pink focus:border-zano-pink outline-none text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const rainConfetti = () => {
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ['#EC4899', '#F9A8D4', '#C4B5FD', '#38BDF8', '#FBCFE8'];
    confetti({
      particleCount: 120,
      spread: 90,
      origin: {
        y: 0.3
      },
      colors
    });
    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 90,
        spread: 70,
        startVelocity: 35,
        origin: {
          x: Math.random(),
          y: -0.1
        },
        gravity: 0.8,
        colors
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const data = new FormData(e.currentTarget);
    const get = (k: string) => data.get(k) as string || '—';
    // Send the submission straight to support@zanoplatform.com via Web3Forms.
    const message = ['ABOUT THE USER', `Full name: ${get('fullName')}`, `Work email: ${get('email')}`, `LinkedIn profile URL: ${get('linkedin')}`, `Job title: ${get('jobTitle')}`, `Country: ${get('country')}`, '', 'COMPANY DETAILS', `Company name: ${get('companyName')}`, `Company website: ${get('website')}`, `Business type: ${get('businessType')}`, '', 'CAMPAIGN DETAILS', `Influencer campaigns per month: ${get('campaignsPerMonth')}`, `Estimated monthly budget: ${get('monthlyBudget')}`, `Type of talent looking for: ${get('talentType')}`, '', 'SUBSCRIPTION', `Currently subscribed to the Casting plan: ${get('castingPlan')}`, '', 'OTHER INFO', `How did you hear about Zano: ${get('howDidYouHear')}`, `Referred by a Zano user?: ${get('referredBy')}`, `Anything else?: ${get('notes')}`].join('\n');
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: 'New Marketplace Access Request',
      from_name: 'Zano — Request Marketplace Access form',
      replyto: get('email'),
      message
    };
    try {
      setSubmitting(true);
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || 'Submission failed');
      }
      rainConfetti();
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong sending your request. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-1055" data-mp="2jb407pfpvr7u">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl overflow-hidden relative max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1056" data-mp="2t7zzr5nn1309">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10" data-id="element-1057" data-mp="375ojga5yy43q">
          <X size={20} data-id="element-1058" data-mp="3gw9ifypk1an6" />
        </button>

        {submitted ? <div className="p-8 sm:p-10 text-center" data-id="element-1059" data-mp="3bxy7tlrfqw3w">
            <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50" data-id="element-1060" data-mp="3bz9j06n7n651">
              <CheckCircle2 size={32} className="text-emerald-500" data-id="element-1061" data-mp="3pvwioh6jsaen" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1062" data-mp="3lukpy2sdn928">
              Request received!
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-1063" data-mp="8dr0n1zyt38q">
              Thank you, we received your request. Our team will get back to you
              within 48 hours. If you are approved, you will receive an
              invitation to create an account as a marketplace user and
              subscribe to a free trial on the marketplace plan. Any questions,
              email us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zano-pink hover:underline" data-id="element-1064" data-mp="3ql6nozvovpm7">
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <Button variant="primary" className="w-full sm:w-auto justify-center px-8" onClick={onClose} data-id="element-1065" data-mp="3guvfh14z2u9i">
              Done
            </Button>
          </div> : <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-1066" data-mp="37u5qaga7w24r">
            <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1067" data-mp="1de8gj3y28w39">
              Request Marketplace Access
            </h3>
            <p className="text-gray-600 mb-8 text-sm leading-relaxed" data-id="element-1068" data-mp="18mpan7h9qzyv">
              Get access to talent agency rosters, represented influencer
              profiles, marketplace benchmarks, average pricing insights, and
              the Zano Dashboard. Complete the form below and our team will
              review your request.
            </p>

            <form className="space-y-6" onSubmit={handleSubmit} data-id="element-1069" data-mp="1wb04hqojrzdn">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-1070" data-mp="1sc967g8ciboy">
                <div data-id="element-1071" data-mp="1nbv6clgwe820">
                  <label className={labelClass} data-id="element-1072" data-mp="1nbd5tcn7d7oa">Full name</label>
                  <input name="fullName" type="text" required className={inputClass} data-id="element-1073" data-mp="1ie369463nfmq" />
                </div>
                <div data-id="element-1074" data-mp="6y51vdlv9hru">
                  <label className={labelClass} data-id="element-1075" data-mp="6xmy7vwvk19w">Work email</label>
                  <input name="email" type="email" required className={inputClass} data-id="element-1076" data-mp="3t0dr7nbvnr84" />
                </div>
                <div data-id="element-1077" data-mp="6s42zudp9tjt">
                  <label className={labelClass} data-id="element-1078" data-mp="6rvpc09r6k7k">LinkedIn profile URL</label>
                  <input name="linkedin" type="url" required className={inputClass} data-id="element-1079" data-mp="bjvt63sy1x9f" />
                </div>
                <div data-id="element-1080" data-mp="kj9656ga13yw">
                  <label className={labelClass} data-id="element-1081" data-mp="kir5lxml03l6">Job title</label>
                  <input name="jobTitle" type="text" required className={inputClass} data-id="element-1082" data-mp="piluxwqpkl44" />
                </div>
                <div data-id="element-1083" data-mp="2rb9zqaj03gm0">
                  <label className={labelClass} data-id="element-1084" data-mp="2rbth9q4qo2zi">Country</label>
                  <input name="country" type="text" required className={inputClass} data-id="element-1085" data-mp="2w9blx4bq3w1p" />
                </div>
                <div data-id="element-1086" data-mp="x368hqwkntkt">
                  <label className={labelClass} data-id="element-1087" data-mp="x1mmrks5cqbn">Company name</label>
                  <input name="companyName" type="text" required className={inputClass} data-id="element-1088" data-mp="1braogn48pqzw" />
                </div>
                <div data-id="element-1089" data-mp="j2dcf1qmgqfl">
                  <label className={labelClass} data-id="element-1090" data-mp="j3obwpd1bc0w">Company website</label>
                  <input name="website" type="url" required className={inputClass} data-id="element-1091" data-mp="e4bpg5q2s6yu" />
                </div>
                <div data-id="element-1092" data-mp="o1gd69zkkahr">
                  <label className={labelClass} data-id="element-1093" data-mp="o16oqqu6abe6">
                    What kind of business are you?
                  </label>
                  <select name="businessType" required className={inputClass} data-id="element-1094" data-mp="23ktblv4ksp93">
                    <option value="" disabled selected data-id="element-1095" data-mp="1zglkjranax7p">
                      Select an option
                    </option>
                    <option data-id="element-1096" data-mp="272qt1c6qjqms">Brand</option>
                    <option data-id="element-1097" data-mp="2c0a82eb9c7fs">Marketing Agency</option>
                    <option data-id="element-1098" data-mp="2bxnxe5t2lbtc">Talent Agency</option>
                  </select>
                </div>
                <div data-id="element-1099" data-mp="1x4krwwsr6oiz">
                  <label className={labelClass} data-id="element-1100" data-mp="221kz5b1c9xbo">
                    How many influencer campaigns do you run each month?
                  </label>
                  <input name="campaignsPerMonth" type="text" required className={inputClass} data-id="element-1101" data-mp="2psgjxo7dns9f" />
                </div>
                <div data-id="element-1102" data-mp="2g12fd20kk29f">
                  <label className={labelClass} data-id="element-1103" data-mp="2fzhvc6bvs1pt">
                    Estimated monthly budget for influencer marketing
                  </label>
                  <input name="monthlyBudget" type="text" required className={inputClass} data-id="element-1104" data-mp="38oylsyfxx8je" />
                </div>
                <div className="sm:col-span-2" data-id="element-1105" data-mp="1kvyejt20qtxp">
                  <label className={labelClass} data-id="element-1106" data-mp="1ow8hsxyff68v">
                    What kind of talent are you looking to work with?
                  </label>
                  <input name="talentType" type="text" placeholder="e.g. influencers, celebrities, public figures, athletes, B2B LinkedIn" required className={inputClass} data-id="element-1107" data-mp="1aveakin47bbs" />
                </div>
                <div className="sm:col-span-2" data-id="element-1108" data-mp="15yxjpgoj6iv0">
                  <label className={labelClass} data-id="element-1109" data-mp="s67wxa16h9wu">
                    How did you hear about Zano?
                  </label>
                  <select name="howDidYouHear" required className={inputClass} data-id="element-1110" data-mp="x49hy0i5zjay">
                    <option value="" disabled selected data-id="element-1111" data-mp="i81tgnzq3vpv">
                      Select an option
                    </option>
                    <option data-id="element-1112" data-mp="n5tm5k86zlv4">LinkedIn</option>
                    <option data-id="element-1113" data-mp="n8zfxcrrnbx6">YouTube</option>
                    <option data-id="element-1114" data-mp="3kbc9yty9xss">Google</option>
                    <option data-id="element-1115" data-mp="3lcvwrp17sm4">Word of mouth</option>
                    <option data-id="element-1116" data-mp="3vpdwjzaucyf1">WIIM</option>
                    <option data-id="element-1117" data-mp="3vqow1mx97k0c">ChatGPT</option>
                    <option data-id="element-1118" data-mp="dizj01ghiqh6">Referral from existing user</option>
                    <option data-id="element-1119" data-mp="dh67o8uxfi2u">Influencer Marketing Hub</option>
                    <option data-id="element-1120" data-mp="8j4pdq5brmsf">Cannes Lions 2026</option>
                    <option data-id="element-1121" data-mp="8miq6tycbnxr">Product Hunt</option>
                    <option data-id="element-1122" data-mp="miw1tmg70v4b">Email outreach</option>
                    <option data-id="element-1123" data-mp="iic2cc85dff0">The Creator Network</option>
                    <option data-id="element-1124" data-mp="ihkifynwx64k">Other</option>
                  </select>
                </div>
                <div className="sm:col-span-2" data-id="element-1125" data-mp="w8paael1gu4c">
                  <label className={labelClass} data-id="element-1126" data-mp="wbl7u1sz5gs9">
                    Were you referred by a Zano user? If yes, who?
                  </label>
                  <input name="referredBy" type="text" className={inputClass} data-id="element-1127" data-mp="168zoo2it87l3" />
                </div>
                <div className="sm:col-span-2" data-id="element-1128" data-mp="119wiufv09xc2">
                  <label className={labelClass} data-id="element-1129" data-mp="1170iylzlkykz">
                    Anything else you would like us to know?
                  </label>
                  <textarea name="notes" rows={3} className={inputClass} data-id="element-1130" data-mp="1dal90rog4mqu" />
                </div>
              </div>

              <fieldset className="sm:col-span-2" data-id="element-1131" data-mp="1i96bkxr67iig">
                <legend className={labelClass} data-id="element-1132" data-mp="1i8ejvy0p24xg">
                  Are you currently subscribed to the Casting plan?
                </legend>
                <div className="space-y-2" data-id="element-1133" data-mp="1ryzd15xlmh8z">
                  <div className="flex items-start gap-3" data-id="element-1134" data-mp="1s23o8n9hed95">
                    <input type="radio" id="casting-plan-yes" name="castingPlan" value="Yes — subscribed to Casting plan" required className="mt-0.5 shrink-0 border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-1135" data-mp="1w0ku73h61hoz" />
                    <label htmlFor="casting-plan-yes" className="text-sm leading-relaxed text-gray-700" data-id="element-1136" data-mp="bliwzh7yrq2e">
                      Yes — I understand that if approved, my plan will be
                      upgraded to the Marketplace plan ($239/month after the 30
                      day free trial). I can manage or cancel anytime.
                    </label>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1137" data-mp="3lkb0qusb9qc8">
                    <input type="radio" id="casting-plan-no" name="castingPlan" value="No — not subscribed to Casting plan" className="mt-0.5 shrink-0 border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-1138" data-mp="n1nnsnns5yca" />
                    <label htmlFor="casting-plan-no" className="text-sm leading-relaxed text-gray-700" data-id="element-1139" data-mp="83bqcesl2lk2">
                      I'm not currently subscribed to the Casting plan
                    </label>
                  </div>
                </div>
              </fieldset>

              <div className="flex items-start gap-3 pt-2" data-id="element-1140" data-mp="lh182dunh2fr">
                <input type="checkbox" id="marketplace-tos" name="tos" required className="mt-1 shrink-0 rounded border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-1141" data-mp="qb5d2rkcv0e7" />
                <label htmlFor="marketplace-tos" className="text-[11px] text-gray-500 leading-relaxed" data-id="element-1142" data-mp="1493r1vurkut6">
                  By checking this box, I acknowledge and agree to the Terms of
                  Service and Privacy Policy by Zano Platform. I acknowledge
                  that Zano Platform may collect, process, and store my personal
                  data provided in this form for the purpose of evaluating my
                  request and providing access to the platform. I understand
                  that my data will be handled in accordance with the Zano
                  Privacy Policy and GDPR regulations.
                </label>
              </div>

              {error && <p className="text-sm text-red-600" role="alert" data-id="element-1143" data-mp="1x5w7p7j91550">
                  {error}
                </p>}
              <Button variant="primary" className="w-full justify-center py-3 disabled:opacity-60 disabled:cursor-not-allowed" type="submit" disabled={submitting} data-id="element-1144" data-mp="2728z1yrbn6wl">
                {submitting ? 'Sending…' : 'Request Marketplace Access'}
              </Button>
            </form>
          </div>}
      </div>
    </div>;
};